'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { popstore } from '@ps/types';

/**
 * "Magic Clip" video-bg behaviour — 1:1 port of the legacy hook duplicated
 * across text/contact-card/promo/avatar-me/tipping. The client behaviour that
 * forces those variants off the server; isolating it here lets each variant
 * keep a PURE server view and only mount {@link MagicVideoBg} when a clip is
 * configured.
 *
 * Rules (mirroring legacy `expo-av` flow):
 *   - Video plays only when `videoBg.enabled === true`.
 *   - Source URL: `videoBg.file.url` in editor preview,
 *     `videoBg.file.processedUrl` (with `.url` fallback) in live mode.
 *   - After every "ended" event the play count increments. Once it has played
 *     twice AND there is a fallback image/icon to swap to, the video stops and
 *     the static content paints. If there's no fallback, the video loops.
 *   - Plays only while the container is in the viewport (IntersectionObserver);
 *     scrolling out pauses it, scrolling back in resets the counter.
 *   - When the fallback image/icon URL changes, the play counter resets.
 *
 * Takes the `videoBg` slice directly (not the whole button) to stay decoupled.
 */
interface Options {
  videoBg: popstore.ButtonBgAttrs | undefined;
  /** True in editor preview surfaces — uses the raw upload URL; live falls
   *  back to `processedUrl`. */
  preview?: boolean;
  /** URL of the still image/icon that takes over once the video has played
   *  twice. `undefined` → the video loops forever. */
  fallbackUrl?: string | undefined | null;
}

interface VideoElProps {
  ref: React.RefObject<HTMLVideoElement | null>;
  autoPlay: true;
  muted: true;
  playsInline: true;
  onEnded: () => void;
  onLoadedMetadata: () => void;
}

interface Result {
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Resolved clip source. Every consumer must test this for TRUTHINESS, not
   *  nullishness — an unprocessed clip can resolve to `''`, which is no more
   *  playable than `undefined`. */
  videoUrl: string | undefined;
  /** True when the video element should render (false once played twice with a
   *  fallback present, or while off-screen). */
  showVideo: boolean;
  /**
   * True when the variant should paint its still content (image/icon). Upstream's
   * `showButtonIcons = (imageOrIconUrl && !videoUrl) || showNormalContent`: a
   * configured clip suppresses the still content until it has played twice —
   * NOT merely while it is on-screen. Gating on `!showVideo` instead would flash
   * the image on first paint and for every off-screen button, since `isVisible`
   * starts false.
   */
  showStaticContent: boolean;
  /** Aspect ratio for the container while the video shows (undefined once the
   *  image takes over or when not aspect-ratio-locked). */
  aspectRatio: number | undefined;
  /** Spread onto the `<video>` element — wires ref/autoPlay/muted/onEnded. */
  videoProps: VideoElProps;
}

export function useMagicVideoBg({
  videoBg,
  preview,
  fallbackUrl,
}: Options): Result {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [playedCount, setPlayedCount] = useState(0);
  const [showImage, setShowImage] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // Natural-dimensions fallback when stored `videoBg.file.metadata` is missing:
  // measure the `<video>` once it loads metadata so the button doesn't collapse.
  const [naturalRatio, setNaturalRatio] = useState<number | undefined>(
    undefined,
  );

  const videoUrl = videoBg?.enabled
    ? preview
      ? videoBg?.file?.url
      : // `||` not `??`: an unprocessed clip carries `processedUrl: ""`
        // (empty string, not null), and `"" ?? url` would keep the
        // empty string — leaving `videoUrl` falsy so NO <video> ever
        // renders. Fall back to the raw upload URL whenever
        // `processedUrl` is empty/missing.
        videoBg?.file?.processedUrl || videoBg?.file?.url
    : undefined;

  const storedRatio =
    videoBg?.file?.metadata?.width && videoBg?.file?.metadata?.height
      ? videoBg.file.metadata.width / videoBg.file.metadata.height
      : undefined;

  // Aspect-ratio override applies ONLY when the creator has
  // toggled `aspectRatioLocked` on — otherwise leave `undefined`
  // so the container falls back to its normal button height
  // (`minHeight` from the button style). Without this guard, a
  // portrait clip's 9:16 ratio blows the container up to the
  // width divided by 0.56 ≈ button-width × 1.78 tall — covering
  // the entire link-button screen (QA bug #16). Legacy behavior
  // in `flows/bio/pages/home/components/custom-buttons/text.tsx:96-103`.
  //
  // `naturalRatio` (measured from the `<video>` on load) is
  // still captured for the aspect-ratio-locked path so a clip
  // whose stored `metadata` was lost still renders at its true
  // ratio when the toggle is on.
  const aspectRatio =
    !videoUrl || !videoBg?.aspectRatioLocked
      ? undefined
      : (storedRatio ?? naturalRatio);

  // Reset on fallback change — a freshly-uploaded icon retriggers the video.
  useEffect(() => {
    setPlayedCount(0);
    setShowImage(false);
  }, [fallbackUrl]);

  // Once played twice AND we have an image/icon to swap to, hide the video.
  useEffect(() => {
    if (playedCount >= 2 && fallbackUrl) {
      setShowImage(true);
    }
  }, [playedCount, fallbackUrl]);

  // IntersectionObserver — pause off-screen, replay from the start on re-entry.
  useEffect(() => {
    if (!videoUrl) {
      setIsVisible(false);
      return;
    }
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [videoUrl]);

  // On entering the viewport, reset the count so the animation replays.
  useEffect(() => {
    if (isVisible && videoUrl) {
      setPlayedCount(0);
      setShowImage(false);
      const video = videoRef.current;
      if (video) {
        try {
          video.currentTime = 0;
        } catch {
          /* not ready yet — `autoPlay` on the element handles it */
        }
        void video.play().catch(() => {
          /* autoplay blocked — user gesture will start it */
        });
      }
    }
  }, [isVisible, videoUrl]);

  // `onEnded` — bump the count and restart until the swap threshold (or there's
  // no fallback to swap to).
  const onEnded = useCallback(() => {
    setPlayedCount(prev => {
      const next = prev + 1;
      const video = videoRef.current;
      if (video && (next < 2 || !fallbackUrl)) {
        try {
          video.currentTime = 0;
        } catch {
          /* ignore */
        }
        void video.play().catch(() => {});
      }
      return next;
    });
  }, [fallbackUrl]);

  const showVideo = Boolean(videoUrl) && !showImage && isVisible;
  const showStaticContent = !videoUrl || showImage;

  const onLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      setNaturalRatio(video.videoWidth / video.videoHeight);
    }
  }, []);

  const videoProps: VideoElProps = {
    ref: videoRef,
    autoPlay: true,
    muted: true,
    playsInline: true,
    onEnded,
    onLoadedMetadata,
  };

  return {
    containerRef,
    videoUrl,
    showVideo,
    showStaticContent,
    aspectRatio: showImage ? undefined : aspectRatio,
    videoProps,
  };
}
